import ChevronLeft from '@mui/icons-material/ChevronLeft'
import Home from '@mui/icons-material/Home'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import Person from '@mui/icons-material/Person'
import AccountCircle from '@mui/icons-material/AccountCircle'
import { Avatar, IconButton, Menu, MenuItem, Divider, Box, Typography, Tooltip } from '@mui/material'
import { useContext, useEffect, useState, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { GenericDisplayFlex, GenericDisplayFlexColumn } from 'src/mentor/layout/Styles'
import { logoutHandlerSupport } from '../utility/supportGeneralUtils'
import {
  SupportHeaderContainer,
  SupportHeaderGreeting,
  SupportHeaderNavBlock,
  SupportHeaderTitleBlock,
} from './headerStyledComponents'
import { SupportContext } from './SupportPortalLayout'
import useServerAuth from 'src/customHooks/useServerAuth'
import ProfileDrawer from '../components/profile/ProfileDrawer'
import SupportAgentProfile from '../components/profile/SupportAgentProfile'
import { supportRoutes } from './routes'
import PortalSwitcherButton from '../components/header/PortalSwitcherButton'
import SupportAccessInfoDrawer from '../components/header/SupportAccessInfoDrawer'
import { hasSupportAccessMatrixAccess } from '../access/accessMatrix'
import { getUserRoles } from 'src/mentor/layout/utils'

export default function SupportHeader({ heading, subHeading, showBackButton = true, backPath, children }) {
  const navigate = useNavigate()
  const location = useLocation()
  const suppContext = useContext(SupportContext)
  const toggleSidebar = suppContext?.toggleSidebar
  const { allRolesInBoolean } = useServerAuth()

  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const [supportAgentDrawerOpen, setSupportAgentDrawerOpen] = useState(false)
  const [clinicSuccessManagerDrawerOpen, setClinicSuccessManagerDrawerOpen] = useState(false)
  const [accessInfoDrawerOpen, setAccessInfoDrawerOpen] = useState(false)
  
  // Temporarily disabled scroll behavior - header is always visible and fixed at top
  const [isHeaderVisible] = useState(true) // Always visible
  

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const hasSupportAgentRole =
    allRolesInBoolean.isL2TherapySupport ||
    allRolesInBoolean.isL3TherapySupport

  const hasClinicSuccessManagerRole = allRolesInBoolean.isClinicSuccessManager
  const canViewAccessMatrix = hasSupportAccessMatrixAccess(getUserRoles() || [])

  const handleSupportAgentProfileClick = () => {
    setSupportAgentDrawerOpen(true)
    handleClose()
  }

  const handleClinicSuccessManagerProfileClick = () => {
    setClinicSuccessManagerDrawerOpen(true)
    handleClose()
  }
  const updateWindowWidth = () => {
    setWindowWidth(window.innerWidth)
  }
  useEffect(() => {
    window.addEventListener('resize', updateWindowWidth)

    // Clean up the event listener on component unmount
    return () => window.removeEventListener('resize', updateWindowWidth)
  }, []) // Empty dependency array ensures the event listener is only added once

  // Scroll behavior temporarily disabled - header is fixed at top

  // Function to get page name from route configuration (path is full pathname e.g. /support-home/...)
  const getPageNameFromPath = (path) => {
    if (!path) return null

    // Use fullPath for matching (route.path is relative for RR v6; fullPath is full pathname)
    const pathToMatch = (r) => r.fullPath ?? r.path

    // Try exact match first
    let route = supportRoutes.find(r => {
      const full = pathToMatch(r)
      if (r.exact && full === path) return true
      if (!r.exact && path.startsWith(full)) return true
      return false
    })

    // If no exact match, try pattern matching for dynamic routes
    if (!route) {
      route = supportRoutes.find(r => {
        const full = pathToMatch(r)
        if (full.includes(':')) {
          const pattern = full.replace(/:[^/]+/g, '[^/]+')
          const regex = new RegExp(`^${pattern}$`)
          return regex.test(path)
        }
        return false
      })
    }

    return route?.name || null
  }

  // Get destination page info for tooltip
  const backDestinationInfo = useMemo(() => {
    // Check if there's a previous page in location state
    if (location.state?.from) {
      const pageName = getPageNameFromPath(location.state.from)
      if (pageName) return pageName
      return location.state.fromName || `Previous page`
    }
    
    // Check if there's a referrer and it's from the same origin
    if (document.referrer && document.referrer.startsWith(window.location.origin)) {
      const referrerPath = new URL(document.referrer).pathname
      if (referrerPath !== location.pathname) {
        const pageName = getPageNameFromPath(referrerPath)
        if (pageName) return pageName
        return `Previous page`
      }
    }
    
    // Fallback: go one step up in the URL path
    const pathParts = location.pathname.split('/').filter(Boolean)
    if (pathParts.length > 1) {
      pathParts.pop()
      const parentPath = '/' + pathParts.join('/')
      const pageName = getPageNameFromPath(parentPath)
      if (pageName) return pageName
      
      // Generate a readable name from the path
      const lastSegment = pathParts[pathParts.length - 1]
      if (lastSegment) {
        return lastSegment.split('-').map(word => 
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ')
      }
    }
    
    return 'Previous page'
  }, [location.state, location.pathname])

  // Smart back button handler
  const handleBackClick = () => {
    // If a custom back path is provided, use it
    if (backPath) {
      navigate(backPath)
      return
    }
    
    // Check if there's a previous page in location state
    if (location.state?.from) {
      navigate(location.state.from)
      return
    }
    
    // Special handling for appointments pages
    const pathname = location.pathname
    if (pathname.includes('/appointments')) {
      // Clinic appointments -> clinics listing
      if (pathname.includes('/clinics-hospitals/clinics/') && pathname.endsWith('/appointments')) {
        navigate('/support-home/clinics-hospitals/clinics')
        return
      }
      // Therapist appointments -> therapists listing
      if (pathname.includes('/practitioner-management/therapists/') && pathname.endsWith('/appointments')) {
        navigate('/support-home/practitioner-management/therapists')
        return
      }
      // Doctor appointments -> doctors listing
      if (pathname.includes('/practitioner-management/doctors/') && pathname.endsWith('/appointments')) {
        navigate('/support-home/practitioner-management/doctors')
        return
      }
    }
    
    // Special handling for roster management pages
    if (pathname.includes('/roster-management')) {
      // Navigate back to support home
      navigate('/support-home')
      return
    }
    
    // Special handling for audit pages
    if (pathname.includes('/practitioner-management/auditors/audit')) {
      // If on audit-requests, go back to audit page
      if (pathname.includes('/audit-requests')) {
        navigate('/support-home/practitioner-management/auditors/audit')
        return
      }
      // If on audit page, go back to therapists or home
      if (pathname.endsWith('/audit')) {
        // Check if coming from therapists
        const params = new URLSearchParams(location.search)
        if (params.get('auditorMentorIds')) {
          navigate('/support-home/practitioner-management/therapists')
        } else {
          navigate('/support-home')
        }
        return
      }
    }
    
    // Check if there's a referrer and it's from the same origin
    if (document.referrer && document.referrer.startsWith(window.location.origin)) {
      const referrerPath = new URL(document.referrer).pathname
      // Only go back if referrer is different from current path
      if (referrerPath !== location.pathname) {
        navigate(-1)
        return
      }
    }
    
    // Fallback: go one step up in the URL path
    const pathParts = location.pathname.split('/').filter(Boolean)
    if (pathParts.length > 1) {
      // Remove the last segment
      pathParts.pop()
      const parentPath = '/' + pathParts.join('/')
      navigate(parentPath)
    } else {
      // If we're at root level, just go back in history
      navigate(-1)
    }
  }

  return (
    <>
      <SupportHeaderContainer 
        className="hide-on-print-only"
        data-portal-header="true"
        data-portal="support"
        style={{
          // Always visible, fixed at top
          opacity: 1,
          transform: 'translateY(0)',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flex: 1,
            minWidth: 0,
            gap: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <SupportHeaderNavBlock>
            <IconButton
              onClick={() => navigate('/support-home')}
              sx={{
                color: 'rgba(255, 255, 255, 0.95) !important',
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                backdropFilter: 'blur(12px)',
                width: 40,
                height: 40,
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                transition: 'all 0.2s ease',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.22)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                  borderColor: 'rgba(255, 255, 255, 0.35)',
                },
                '&:active': { transform: 'scale(0.98)' },
              }}
              title="Go to Support Home"
              aria-label="Go to Support Home"
            >
              <Home sx={{ fontSize: 20 }} />
            </IconButton>
            {showBackButton && (
              <Tooltip
                title={backDestinationInfo}
                arrow
                placement="bottom"
                enterDelay={300}
                leaveDelay={100}
                componentsProps={{
                  tooltip: {
                    sx: {
                      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.95) 0%, rgba(79, 70, 229, 0.95) 50%, rgba(99, 102, 241, 0.95) 100%)',
                      backdropFilter: 'blur(12px) saturate(180%)',
                      WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                      fontSize: '13px',
                      padding: '8px 12px',
                      borderRadius: '10px',
                      boxShadow: '0 4px 16px rgba(67, 56, 202, 0.3), 0 2px 8px rgba(0, 0, 0, 0.15)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      color: 'rgba(255, 255, 255, 0.98)',
                      fontWeight: 500,
                      maxWidth: '300px',
                      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
                    },
                  },
                  arrow: { sx: { color: 'rgba(67, 56, 202, 0.95)' } },
                }}
              >
                <IconButton
                  onClick={handleBackClick}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.95) !important',
                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    backdropFilter: 'blur(12px)',
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.22)',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)',
                      borderColor: 'rgba(255, 255, 255, 0.35)',
                    },
                    '&:active': { transform: 'scale(0.98)' },
                  }}
                  aria-label="Go back"
                >
                  <ChevronLeft sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            )}
          </SupportHeaderNavBlock>
          {(heading || subHeading) && (
            <SupportHeaderTitleBlock>
              {heading && <SupportHeaderGreeting main>{heading}</SupportHeaderGreeting>}
              {subHeading && <SupportHeaderGreeting>{subHeading}</SupportHeaderGreeting>}
            </SupportHeaderTitleBlock>
          )}
        </Box>
        {children && <div style={{ position: 'relative', zIndex: 1 }}>{children}</div>}
        <GenericDisplayFlex style={{ position: 'relative', zIndex: 1, gap: '8px', alignItems: 'center' }}>
          <PortalSwitcherButton />
          {canViewAccessMatrix && (
            <Tooltip title="Role-wise access map" arrow placement="bottom">
              <IconButton
                onClick={() => setAccessInfoDrawerOpen(true)}
                sx={{
                  color: 'rgba(255, 255, 255, 0.95) !important',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  width: 40,
                  height: 40,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'scale(1.05)',
                  },
                }}
                aria-label="Open support access matrix"
              >
                <InfoOutlined sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
          
          <IconButton
            onClick={handleClick}
            sx={{
              color: 'rgba(255, 255, 255, 0.95) !important',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              width: 40,
              height: 40,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                transform: 'scale(1.05)',
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <Person sx={{ fontSize: 20 }} />
            </Avatar>
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 8px 24px rgba(67, 56, 202, 0.25))',
                mt: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(67, 56, 202, 0.1)',
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  fontFamily: "'Inter', sans-serif",
                  fontSize: '14px',
                  padding: '10px 16px',
                  '&:hover': {
                    backgroundColor: 'rgba(67, 56, 202, 0.08)',
                  },
                },
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.98)',
                  borderLeft: '1px solid rgba(67, 56, 202, 0.1)',
                  borderTop: '1px solid rgba(67, 56, 202, 0.1)',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {(hasSupportAgentRole || hasClinicSuccessManagerRole) && (
              <>
                {hasSupportAgentRole && (
                  <MenuItem
                    onClick={handleSupportAgentProfileClick}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      },
                    }}
                  >
                    <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
                    Support Agent Profile
                  </MenuItem>
                )}
                {hasClinicSuccessManagerRole && (
                  <MenuItem
                    onClick={handleClinicSuccessManagerProfileClick}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(67, 56, 202, 0.08)',
                      },
                    }}
                  >
                    <AccountCircle sx={{ mr: 1.5, fontSize: 20 }} />
                    Clinic Success Manager Profile
                  </MenuItem>
                )}
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            <MenuItem
              onClick={() => {
                navigate('/')
                handleClose()
              }}
            >
              Back to Consumer View
            </MenuItem>
            <MenuItem
              onClick={() => {
                logoutHandlerSupport()
                navigate('/')
                handleClose()
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </GenericDisplayFlex>
      </SupportHeaderContainer>

      {/* Support Agent Profile Drawer */}
      {hasSupportAgentRole && (
        <ProfileDrawer
          open={supportAgentDrawerOpen}
          onClose={() => setSupportAgentDrawerOpen(false)}
          title="Support Agent Profile"
        >
          <SupportAgentProfile onClose={() => setSupportAgentDrawerOpen(false)} />
        </ProfileDrawer>
      )}

      {/* Clinic Success Manager Profile Drawer - Placeholder for now */}
      {hasClinicSuccessManagerRole && (
        <ProfileDrawer
          open={clinicSuccessManagerDrawerOpen}
          onClose={() => setClinicSuccessManagerDrawerOpen(false)}
          title="Clinic Success Manager Profile"
        >
          <Box p={3}>
            <Typography variant="body1" color="text.secondary">
              Clinic Success Manager profile will be available soon.
            </Typography>
          </Box>
        </ProfileDrawer>
      )}

      <SupportAccessInfoDrawer
        open={accessInfoDrawerOpen}
        onClose={() => setAccessInfoDrawerOpen(false)}
      />
    </>
  )
}
